import { EventEmitter, Injectable } from "@angular/core";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslateService } from "@ngx-translate/core";
import { CreateCarComponent } from "app/modules/components/cars/custom/create-car/create-car.component";
import { CreateFlightTrainComponent } from "app/modules/components/flight-train/create-flight-train/create-flight-train.component";
import { CreateCustomHotelComponent } from "app/modules/components/hotel/create-custom-hotel/create-custom-hotel.component";
import { BookedService } from "./booked.service";
import { CarService } from "./car.service";
import { FlightService } from "./flight.service";
import { NgxToastrService } from "./ngx.toastr.service";
import { TrainService } from "./train.service";
import { CreateAirbnbComponent } from "app/modules/components/airbnb/custom/create-airbnb/create-airbnb.component";
import { AirbnbService } from "./airbnb.service";
import { UtilsService } from './utils.service';
import * as moment from 'moment';

@Injectable({
  providedIn: "root",
})
export class RequestUtilsService {
  sendInfo: EventEmitter<boolean> = new EventEmitter();

  constructor(
    private modalService: NgbModal,
    private flightService: FlightService,
    private trainService: TrainService,
    private bookedService: BookedService,
    private carService: CarService,
    private airbnbService: AirbnbService,
    private translateService: TranslateService,
    private ngxToastrService: NgxToastrService,
    private utilsService: UtilsService
  ) {}

  createCustomFlightTrain(
    request: any,
    type: string,
    hasPrivileges: boolean
  ) {
    const normalizedType = type?.trim().toLowerCase();

    if (
      normalizedType !== "flight" &&
      normalizedType !== "train"
    ) {
      return;
    }

    const modalRef = this.modalService.open(
      CreateFlightTrainComponent,
      {
        size: "lg",
        centered: true,
        backdrop: "static",
        keyboard: false,
      }
    );

    modalRef.componentInstance.isAdmin = hasPrivileges;
    modalRef.componentInstance.type = normalizedType;
    modalRef.componentInstance.loading = false;
    modalRef.componentInstance.companyUuid =
      request.companyUuid;
    modalRef.componentInstance.request =
      JSON.parse(JSON.stringify(request));

    if (request.userUuid) {
      modalRef.componentInstance.userId =
        request.userUuid;
    }

    modalRef.componentInstance.sendInfo.subscribe((info) => {
      modalRef.componentInstance.loading = true;

      const formData = new FormData();

      if (Array.isArray(info.file)) {
        info.file.forEach((file: any, index: number) => {
          if (file?.blob && file?.name) {
            const newFile = new File(
              [file.blob],
              file.name,
              { type: file.blob.type }
            );

            formData.append(
              "file",
              newFile,
              newFile.name
            );
          } else if (file instanceof File) {
            formData.append("file", file, file.name);
          } else if (file instanceof Blob) {
            const newFile = new File(
              [file],
              `file_${index}.pdf`,
              { type: file.type }
            );

            formData.append(
              "file",
              newFile,
              newFile.name
            );
          }
        });
      }

      formData.append(
        "companyUuid",
        request.companyUuid
      );

      formData.append(
        "userUuid",
        request.userUuid
      );

      formData.append(
        "departDate",
        moment(info.departure).format(
          "YYYY-MM-DD HH:mm:ss"
        )
      );

      formData.append(
        "observations",
        info.annotations || ""
      );

      formData.append(
        "oneWay",
        String(!!info.oneWay)
      );

      formData.append(
        "customCode",
        info.customCode || ""
      );

      formData.append(
        "costCenter",
        info.costCenter || ""
      );

      if (info.price != null) {
        formData.append(
          "price",
          String(info.price)
        );
      }

      if (info.freeTaxes != null) {
        formData.append(
          "freeTaxes",
          String(info.freeTaxes)
        );
      }

      if (info.wholesalePrice != null) {
        formData.append(
          "wholesalePrice",
          String(info.wholesalePrice)
        );
      }

      if (info.providerUuid) {
        formData.append(
          "providerUuid",
          info.providerUuid
        );
      }

      if (info.supplierInvoice) {
        formData.append(
          "supplierInvoice",
          info.supplierInvoice
        );
      }

      if (info.bookType) {
        formData.append(
          "bookType",
          info.bookType
        );
      }

      if (info.invoiced) {
        formData.append(
          "invoiced",
          info.invoiced
        );
      }

      if (!info.oneWay && info.return) {
        formData.append(
          "returnDate",
          moment(info.return).format(
            "YYYY-MM-DD HH:mm:ss"
          )
        );
      }

      if (info.pnr) {
        formData.append("pnr", info.pnr);
      }

      if (normalizedType === "flight") {
        formData.append(
          "from",
          typeof info.origin === "string"
            ? info.origin
            : info.origin?.name || ""
        );

        formData.append(
          "to",
          typeof info.destination === "string"
            ? info.destination
            : info.destination?.name || ""
        );

        formData.append("newCustom", "true");

        if (info.departureAirline) {
          formData.append(
            "departureAirline",
            info.departureAirline
          );
        }

        if (
          info.returnAirline &&
          !info.oneWay
        ) {
          formData.append(
            "returnAirline",
            info.returnAirline
          );
        }
      } else {
        const origin =
          typeof info.origin === "string"
            ? { name: info.origin.trim() }
            : info.origin;

        const destination =
          typeof info.destination === "string"
            ? { name: info.destination.trim() }
            : info.destination;

        formData.append(
          "origin",
          JSON.stringify(origin)
        );

        formData.append(
          "destination",
          JSON.stringify(destination)
        );

        formData.append("newCustom", "true");

        if (info.departureCarrier) {
          formData.append(
            "departureCarrier",
            info.departureCarrier
          );
        }

        if (
          info.returnCarrier &&
          !info.oneWay
        ) {
          formData.append(
            "returnCarrier",
            info.returnCarrier
          );
        }
      }

      if (info.traveller) {
        formData.append(
          "traveller",
          JSON.stringify(info.traveller)
        );
      }

      if (info.paymentCard) {
        formData.append(
          "paymentCard",
          JSON.stringify(info.paymentCard)
        );
      }

      if (Array.isArray(info.travellers)) {
        const travellers = info.travellers.map(
          (traveller: any) => {
            const cloned = { ...traveller };

            if (
              Array.isArray(cloned.files) &&
              cloned.files.length > 0
            ) {
              cloned.files.forEach((file: File) => {
                formData.append(
                  "file",
                  file,
                  file.name
                );
              });

              cloned.filesCount =
                cloned.files.length;

              delete cloned.files;
            }

            return cloned;
          }
        );

        formData.append(
          "travellers",
          JSON.stringify(travellers)
        );
      }

      const postCreate =
        normalizedType === "train"
          ? this.trainService.postCreateTrain(
              formData,
              request.uuid
            )
          : this.flightService.postCreateFlight(
              formData,
              request.uuid
            );

      postCreate.subscribe(
        (result) => {
          modalRef.componentInstance.loading = false;
          modalRef.dismiss("Send Info");

          this.translateService
            .get([
              "common.congrat",
              `${normalizedType}.create-${normalizedType}.created-${normalizedType}`,
            ])
            .subscribe((resp) => {
              this.ngxToastrService.typeSuccess(
                resp["common.congrat"],
                resp[
                  `${normalizedType}.create-${normalizedType}.created-${normalizedType}`
                ]
              );
            });

          this.sendInfo.emit(true);
        },
        (err) => {
          modalRef.componentInstance.loading = false;

          const errorBody = err?.error || {};

          const validationErrors =
            Array.isArray(errorBody.validationErrors)
              ? errorBody.validationErrors
              : [];

          if (
            err.status === 422 &&
            validationErrors.length > 0
          ) {
            const validationMessage =
              this.utilsService
                .getValidationErrorsMessage(
                  validationErrors,
                  normalizedType
                );

            if (
              typeof modalRef.componentInstance
                .applyBackendValidationErrors ===
              "function"
            ) {
              modalRef.componentInstance
                .applyBackendValidationErrors(
                  validationErrors
                );
            }

            this.ngxToastrService.typeInfo(
              "ERROR",
              validationMessage ||
                errorBody.message ||
                this.translateService.instant(
                  "common.validation.form-invalid"
                ),
              10000,
              { enableHtml: true }
            );

            return;
          }

          this.translateService
            .get([
              `${normalizedType}.create-${normalizedType}.error-create-${normalizedType}`,
              "common.transaction-error",
            ])
            .subscribe((result) => {
              this.ngxToastrService.typeInfo(
                result[
                  `${normalizedType}.create-${normalizedType}.error-create-${normalizedType}`
                ],
                result["common.transaction-error"]
              );
            });
        }
      );
    });
  }

  createCustomHotel(request: any, hasPrivileges: boolean) {
    const modalHotel = this.modalService.open(CreateCustomHotelComponent, {
      size: "lg",
      centered: true,
      backdrop: "static",
      keyboard: false,
    });
    modalHotel.componentInstance.userId = request.userUuid;
    modalHotel.componentInstance.isAdmin = hasPrivileges;
    modalHotel.componentInstance.companyUuid = request.companyUuid;
    modalHotel.componentInstance.request = JSON.parse(JSON.stringify(request));

    modalHotel.componentInstance.sendInfo.subscribe((info) => {
      modalHotel.componentInstance.loading = true;
      const formData = new FormData();
      if(info.file){
        if (info.file && Array.isArray(info.file)) {
          info.file.forEach((file: any, index: number) => {
            if (file.blob && file.name) {
              const newFile = new File([file.blob], file.name, { type: file.blob.type });
              formData.append("file", newFile, newFile.name);
            } 
            else if (file instanceof File) {
              formData.append("file", file, file.name);
            } 
            else if (file instanceof Blob) {
              const newFile = new File([file], `file_${index}.pdf`, { type: file.type });
              formData.append("file", newFile, newFile.name);
            } 
          });
        } 
      }
      formData.append("userUuid", request.userUuid);
      formData.append("checkIn", info.checkIn);
      formData.append("checkOut", info.checkOut);
      if (info.price) {
        formData.append("price", info.price);
      }
      if (info.wholesalePrice) {
        formData.append("wholesalePrice", info.wholesalePrice);
      }
      formData.append("destinationName", info.city);
      formData.append("rooms", info.rooms);
      if(info.people){
        formData.append("people", info.people);
      }
      formData.append("holder", JSON.stringify(info.holder))
      formData.append("hotelName", info.hotel);
      if(info.freeTaxes){
        formData.append("freeTaxes", info.freeTaxes);
      }
      formData.append("customCode", info.customCode);
      formData.append("costCenter", info.costCenter);
      if(info.marketingFee) {
        formData.append("marketingFee", info.marketingFee);
      }
      if (info.providerUuid) {
        formData.append("providerUuid", info.providerUuid);
      }
      if(info.supplierInvoice) {
        formData.append("supplierInvoice", info.supplierInvoice);
      }
      if(info.bookType) {
        formData.append("bookType", info.bookType);
      }
      if(info.invoiced) {
        formData.append("invoiced", info.invoiced);
      }
      if (info.comments) {
        formData.append("comments", info.comments);
      }
      if(info.paymentCard){
        formData.append("paymentCard", JSON.stringify(info.paymentCard));
      }
      formData.append('newCustom', 'true');
      formData.append('type', 'hotel');
      if(info.locator){
        formData.append("locator", info.locator);
      }
      if (info.travelersPerRoom) {
        const formattedTravelersPerRoom = info.travelersPerRoom.map((travelers, index) => ({
          roomNumber: index + 1,
          travelers: travelers
        }));
        
        formData.append("travelersPerRoom", JSON.stringify(formattedTravelersPerRoom));
      }
      if (info.roomsInfo) {
        info.roomsInfo.forEach((room) => {
          if (room.files && Array.isArray(room.files)) {
            room.filesCount = room.files.length;
            room.files.forEach((file) => {
              formData.append("file", file);
            });
            delete room.files;
          }
        });
        formData.append("roomsInfo", JSON.stringify(info.roomsInfo));
      }
      // const fte = {
      //   departDate: info.checkIn,
      //   returnDate: info.checkOut,
      //   oneWay: false,
      // };
      this.bookedService.createCustomBooking(formData, request.uuid).subscribe(
        (s) => {
          this.translateService
            .get(["common.congrat", "hotel.custom.created"])
            .subscribe((resp) => {
              modalHotel.componentInstance.loading = false;
              this.ngxToastrService.typeSuccess(
                resp["common.congrat"],
                resp["hotel.create-custom.success"]
              );
              modalHotel.close();
              this.sendInfo.emit(true);
            });
        },
        (err) => {
          modalHotel.componentInstance.loading = false;

          const errorBody = err?.error || {};
          const validationErrors = Array.isArray(errorBody.validationErrors)
            ? errorBody.validationErrors
            : [];

          if (err.status === 422 && validationErrors.length > 0) {
            const validationMessage = this.utilsService.getValidationErrorsMessage(
              validationErrors,
              'hotel'
            );

            if (typeof modalHotel.componentInstance.applyBackendValidationErrors === 'function') {
              modalHotel.componentInstance.applyBackendValidationErrors(validationErrors);
            }

            this.ngxToastrService.typeInfo(
              'ERROR',
              validationMessage || errorBody.message || this.translateService.instant('common.validation.form-invalid'),
              10000,
              { enableHtml: true }
            );

            return;
          }

          this.translateService.get("common.error").subscribe((result) => {
            this.ngxToastrService.typeInfo(
              result,
              errorBody.message ? errorBody.message : errorBody.text
            );
          });
        }
      );
    });
  }

  createCustomCar(request: any, hasPrivileges: boolean) {
    const modalRef = this.modalService.open(CreateCarComponent, {
      size: "lg",
      centered: true,
      backdrop: "static",
      keyboard: false,
    });

    modalRef.componentInstance.isAdmin = hasPrivileges;
    modalRef.componentInstance.loading = false;
    modalRef.componentInstance.request = request;
    if (request.userUuid) {
      modalRef.componentInstance.userId = request.userUuid;
    }
    modalRef.componentInstance.sendInfo.subscribe((info) => {
      modalRef.componentInstance.loading = true;
      const formData = new FormData();
      if(info.file){
        if (info.file && Array.isArray(info.file)) {
          info.file.forEach((file: any, index: number) => {
            if (file.blob && file.name) {
              const newFile = new File([file.blob], file.name, { type: file.blob.type });
              formData.append("file", newFile, newFile.name);
            } 
            else if (file instanceof File) {
              formData.append("file", file, file.name);
            } 
            else if (file instanceof Blob) {
              const newFile = new File([file], `file_${index}.pdf`, { type: file.type });
              formData.append("file", newFile, newFile.name);
            } 
          });
        } 
      }
      formData.append("userUuid", request.userUuid);
      formData.append("from", info.from);
      formData.append("to", info.to);
      formData.append("pickupPlaceFrom", info.pickupPlaceFrom);
      formData.append("pickupPlaceTo", info.pickupPlaceTo);
      formData.append("price", info.price);
      formData.append("age", info.age);
      formData.append("freeTaxes", info.freeTaxes);
      formData.append("departDate", info.departDate);
      formData.append("returnDate", info.returnDate);
      if (info.providerUuid) {
        formData.append("providerUuid", info.providerUuid);
      }
      formData.append("carType", info.carType);
      formData.append("insuranceWithExcess", info.insuranceWithExcess);
      formData.append("comments", info.comments);
      formData.append("newCustom", "true");
      if(info.wholesalePrice) {
        formData.append("wholesalePrice", info.wholesalePrice);
      }
      if(info.traveller) {
        formData.append("traveller", JSON.stringify(info.traveller));
      }
      if(info.paymentCard){
        formData.append("paymentCard", JSON.stringify(info.paymentCard));
      }
      if(info.supplierInvoice) {
        formData.append("supplierInvoice", info.supplierInvoice);
      }
      if(info.bookType) {
        formData.append("bookType", info.bookType);
      }
      if(info.invoiced) {
        formData.append("invoiced", info.invoiced);
      }
      // const fte = {
      //   departDate: info.departDate,
      //   returnDate: info.returnDate,
      //   oneWay: false,
      // };
      this.carService.createCustomCar(formData, request.uuid).subscribe(
        (car) => {
          modalRef.componentInstance.loading = false;
          modalRef.dismiss("Send Info");
          this.translateService
            .get(["car.create-car.created-car"])
            .subscribe((result) => {
              this.ngxToastrService.typeSuccess(
                "",
                result["car.create-car.created-car"]
              );
              modalRef.dismiss();
              this.sendInfo.emit(true);
            });
        },
        (err) => {
          modalRef.componentInstance.loading = false;

          const errorBody = err?.error || {};
          const validationErrors = Array.isArray(errorBody.validationErrors)
            ? errorBody.validationErrors
            : [];

          if (err.status === 422 && validationErrors.length > 0) {
            const validationMessage = this.utilsService.getValidationErrorsMessage(
              validationErrors,
              'car'
            );

            if (typeof modalRef.componentInstance.applyBackendValidationErrors === 'function') {
              modalRef.componentInstance.applyBackendValidationErrors(validationErrors);
            }

            this.ngxToastrService.typeInfo(
              'ERROR',
              validationMessage || errorBody.message || this.translateService.instant('common.validation.form-invalid'),
              10000,
              { enableHtml: true }
            );

            return;
          }

          this.translateService
            .get(["car.create-car.error-create-car"])
            .subscribe((result) => {
              this.ngxToastrService.typeInfo(
                "",
                errorBody.message || errorBody.text || result["car.create-car.error-create-car"]
              );

              modalRef.dismiss();
            });
        }
      );
    });
  }

  createCustomAirbnb(request: any, hasPrivileges: boolean) {
    const modalAirbnb = this.modalService.open(CreateAirbnbComponent, {
      size: "lg",
      centered: true,
      backdrop: "static",
      keyboard: false,
    });
    modalAirbnb.componentInstance.userId = request.userUuid;
    modalAirbnb.componentInstance.isAdmin = hasPrivileges;
    modalAirbnb.componentInstance.companyUuid = request.companyUuid;
    modalAirbnb.componentInstance.request = JSON.parse(JSON.stringify(request));

    modalAirbnb.componentInstance.sendInfo.subscribe((info) => {
      modalAirbnb.componentInstance.loading = true;
      const formData = new FormData();
      if(info.file){
        if (info.file && Array.isArray(info.file)) {
          info.file.forEach((file: any, index: number) => {
            if (file.blob && file.name) {
              const newFile = new File([file.blob], file.name, { type: file.blob.type });
              formData.append("file", newFile, newFile.name);
            } 
            else if (file instanceof File) {
              formData.append("file", file, file.name);
            } 
            else if (file instanceof Blob) {
              const newFile = new File([file], `file_${index}.pdf`, { type: file.type });
              formData.append("file", newFile, newFile.name);
            } 
          });
        } 
      }
      formData.append("userUuid", request.userUuid);
      formData.append("checkIn", info.checkIn);
      formData.append("checkOut", info.checkOut);
      formData.append("price", info.price);
      formData.append("destinationName", info.city);
      formData.append("people", info.travelers);
      formData.append("hotelName", info.link || 'Airbnb');
      formData.append("customCode", info.customCode);
      formData.append("costCenter", info.costCenter);
      if(info.marketingFee) {
        formData.append("marketingFee", info.marketingFee);
      }
      if(info.providerUuid) {
        formData.append ("providerUuid", info.providerUuid);
      }
      if (info.wholesalePrice) {
        formData.append("wholesalePrice", info.wholesalePrice);
      }
      if(info.supplierInvoice) {
        formData.append("supplierInvoice", info.supplierInvoice);
      }
      if(info.bookType) {
        formData.append("bookType", info.bookType);
      }
      if(info.invoiced) {
        formData.append("invoiced", info.invoiced);
      }
      if (info.comments) {
        formData.append("comments", info.comments);
      }
      if(info.paymentCard){
        formData.append("paymentCard", JSON.stringify(info.paymentCard));
      }

      formData.append('newCustom', 'true');
      formData.append('type', 'airbnb');
    
      this.airbnbService.createCustomAirbnb(formData, request.uuid).subscribe(
        (s) => {
          this.translateService
            .get(["common.congrat", "hotel.custom.created"])
            .subscribe((resp) => {
              modalAirbnb.componentInstance.loading = false;
              this.ngxToastrService.typeSuccess(
                resp["common.congrat"],
                resp["hotel.create-custom.success"]
              );
              modalAirbnb.close();
              this.sendInfo.emit(true);
            });
        },
        (err) => {
          modalAirbnb.componentInstance.loading = false;

          const errorBody = err?.error || {};
          const validationErrors = Array.isArray(errorBody.validationErrors)
            ? errorBody.validationErrors
            : [];

          if (err.status === 422 && validationErrors.length > 0) {
            const validationMessage = this.utilsService.getValidationErrorsMessage(
              validationErrors,
              'airbnb'
            );

            if (typeof modalAirbnb.componentInstance.applyBackendValidationErrors === 'function') {
              modalAirbnb.componentInstance.applyBackendValidationErrors(validationErrors);
            }

            this.ngxToastrService.typeInfo(
              'ERROR',
              validationMessage || errorBody.message || this.translateService.instant('common.validation.form-invalid'),
              10000,
              { enableHtml: true }
            );

            return;
          }

          this.translateService.get("common.error").subscribe((result) => {
            this.ngxToastrService.typeInfo(
              result,
              errorBody.message || errorBody.text || this.translateService.instant('common.some-error')
            );
          });
        }
      );
    });
  }
}
